import {
  getOssFileUrl,
  getOssUploadCredential,
  ossUploadCallback,
  uploadFileToOss,
} from "@/api/oss";

const OSS_PUBLIC_URL_PREFIX = "https://pic.xsou.com/";

/**
 * 获取文件可访问地址。
 * 公开文件已经是 https://pic.xsou.com/ 开头时直接返回；
 * 私有文件保存的是OSS key，需要调用 /oss/file 换取临时访问URL。
 * @param {string|Object} source - 文件URL、OSS key，或包含 url 字段的文件对象
 */
export async function getOssReadableUrl(source) {
  const fileKeyOrUrl = getFileKeyOrUrl(source);
  if (!fileKeyOrUrl) return "";
  // 公开文件或本地临时预览地址直接返回；私有OSS key再换临时访问URL。
  if (
    fileKeyOrUrl.startsWith(OSS_PUBLIC_URL_PREFIX) ||
    /^(https?:|blob:|data:)/.test(fileKeyOrUrl)
  ) {
    return fileKeyOrUrl;
  }
  // 私有文件保存的是OSS key，需要调用 /oss/file 换取临时访问URL
  const resp = await getOssFileUrl({ fileKey: fileKeyOrUrl });
  return getReadableUrlFromResp(resp, fileKeyOrUrl);
}

// 从OSS接口返回的响应中提取可访问的URL
function getReadableUrlFromResp(resp, fallback) {
  if (!resp) return fallback;
  if (resp.code && resp.code !== 200) return fallback;
  if (resp.data) return resp.data;
  if (resp.msg) return resp.msg; //返回加密图片链接
  return fallback;
}

// 从上传源中提取文件key或URL
function getFileKeyOrUrl(source) {
  if (!source) return "";
  if (typeof source === "string") return source;
  return source.url;
}
// 从上传源中提取文件格式（支持File、Blob、FormData、对象）
function getFileFromUploadSource(source) {
  if (
    (typeof File !== "undefined" && source instanceof File) ||
    (typeof Blob !== "undefined" && source instanceof Blob)
  ) {
    return source;
  }

  if (typeof FormData !== "undefined" && source instanceof FormData) {
    return source.get("file");
  }

  if (source && source.file) {
    return source.file;
  }

  return null;
}

/**
 * 通过OSS直传文件的封装后的方法
 * @param {File|Blob|FormData|Object} source - File、Blob、包含file字段的对象，或包含file字段的FormData
 * @param {number} bizType - 业务类型: 1用户信息、2商品信息、3合同信息、4其他
 * @param {number} maxSize - 调用方允许的最大文件大小，可选
 * @param {number} bizId - 业务ID，必需
 * @param {boolean} isPublic - 是否公开可读, 除了商品信息其他都填false，必需
 * @param {number} groupId - 文件库分组ID，仅文件库弹窗本地上传时传入
 */
export async function uploadByOss(
  source,
  bizType = 4,
  maxSize = 1024 * 1024 * 10,
  bizId,
  isPublic = false,
  groupId
) {
  const file = getFileFromUploadSource(source);
  if (!file) {
    throw new Error("请选择要上传的文件");
  }
  if (file.size > maxSize) {
    throw new Error(`文件大小不能超过${Math.ceil(maxSize / 1024 / 1024)}MB`);
  }

  let credential = null;
  let url = "";
  let uploadStage = "credential";
  try {
    // 第一步：向业务后端申请OSS上传凭证。
    // 后端会根据文件名、大小、业务类型、业务ID和公开权限生成 key、policy、签名等信息。
    // 注意：这里还没有上传真实文件，只是在拿“允许上传到OSS的临时通行证”。
    const credentialResp = await getOssUploadCredential({
      fileName: file.name || "未知",
      fileSize: file.size,
      bizType,
      bizId,
      isPublic,
      groupId,
    });

    if (credentialResp.code !== 200) {
      return { ...credentialResp, __errorHandled: true };
    }
    if (!credentialResp.data) {
      return { code: 500, msg: "获取OSS上传凭证返回数据异常" };
    }

    // 第二步：使用凭证直连OSS上传文件。
    // OSS成功时通常返回HTTP 204且没有业务JSON，所以这里看 uploadResp.status 是否为204。
    // 这一段不要走普通业务接口格式判断，否则容易把204无内容误判成失败。
    credential = credentialResp.data;
    url = isPublic ? credential.fileUrl : credential.key;
    uploadStage = "oss";
    const uploadResp = await uploadFileToOss({
      uploadUrl: credential.uploadUrl,
      key: credential.key,
      policy: credential.policy,
      OSSAccessKeyId: credential.ossAccessKeyId,
      signature: credential.signature,
      contentType: credential.contentType,
      file,
    });
    if (!uploadResp || uploadResp.status !== 204) {
      await ossUploadCallback({ key: credential.key, status: 0 });
      return { code: 500, msg: "OSS文件上传失败" };
    }
    // 第三步：通知业务后端“这个key对应的文件已经上传成功”。
    // 后端可以在这里落库、绑定文件记录，或者更新文件状态。
    uploadStage = "callback";
    const callbackResp = await ossUploadCallback({
      key: credential.key,
      status: 1,
    });
    if (callbackResp.code !== 200) {
      return { ...callbackResp, __errorHandled: true };
    }
    // 普通 OSS 上传只依赖上传结果；文件库入口会额外校验回调中的真实数据库 ID。
    // 这样文件库不会使用临时记录，同时避免公共上传组件被文件库专属返回结构绑死。
    const uploadedFile = callbackResp.data || {};
    return {
      code: 200,
      ...uploadedFile,
      url: uploadedFile.url || url,
      fileKey: uploadedFile.fileKey || (!isPublic ? url : ""),
      isPublic,
      name: uploadedFile.originalName || file.name,
      originalName: uploadedFile.originalName || file.name,
      msg: callbackResp.msg || "上传成功",
    };
  } catch (error) {
    // 异常处理：如果已经拿到key但后续上传失败，需要回调后端 status=0。
    // 这样后端能清理或标记失败文件，避免出现“拿了凭证但文件不存在”的脏记录。
    if (uploadStage === "oss" && credential && credential.key) {
      try {
        await ossUploadCallback({
          key: credential.key,
          status: 0,
        });
      } catch {
        // 补偿回调失败不能覆盖原始OSS上传错误。
      }
    }
    if (uploadStage !== "oss") error.__errorHandled = true;
    throw error;
  }
}
